/**
 * 统一设计Token系统
 * Unified Design Token System
 *
 * 基于翡翠绿色系(Emerald)的设计规范
 * 确保整个应用的视觉一致性
 *
 * @version 1.0.0
 * @author Green Bond Platform Team
 * @date 2025-01-17
 */

/**
 * 🎨 翡翠绿主题色系
 *
 * 主色调：#10b981 (翡翠绿500)
 * 使用场景：品牌色、主要操作按钮、重要状态标识
 */
export const EMERALD_THEME = {
  /**
   * 颜色系统
   */
  colors: {
    /**
     * 主色调 - 翡翠绿
     * 从最浅(50)到最深(900)的完整色阶
     */
    primary: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981', // 🌟 主色
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },

    /**
     * 语义化颜色
     * 用于传达特定含义的颜色
     */
    semantic: {
      success: '#10b981', // 成功、完成
      warning: '#f59e0b', // 警告、进行中
      error: '#ef4444', // 错误、失败
      info: '#3b82f6', // 信息、提示
    },

    /**
     * 状态颜色
     * 用于工作流状态标识
     */
    status: {
      pending: '#94a3b8', // 待处理
      in_progress: '#f59e0b', // 进行中
      completed: '#10b981', // 已完成
      cancelled: '#ef4444', // 已取消
      approved: '#10b981', // 已批准
      rejected: '#ef4444', // 已拒绝
      supplement: '#faad14', // 需补充
      draft: '#d9d9d9', // 草稿
      submitted: '#1890ff', // 已提交
      under_review: '#722ed1', // 审核中
    },

    /**
     * 统计卡片颜色
     * 用于Banner区域的统计数据展示
     */
    statistics: {
      total: '#262626', // 总数
      active: '#10b981', // 进行中/活跃
      completed: '#52c41a', // 已完成
      pending: '#1890ff', // 待处理
      rejected: '#ff4d4f', // 已拒绝
      warning: '#faad14', // 警告/需补充
    },

    /**
     * 中性色
     * 用于背景、边框、文本等
     */
    neutral: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },

    /**
     * 功能色
     * 特殊用途的颜色
     */
    functional: {
      background: '#ffffff',
      border: '#e2e8f0',
      divider: '#e2e8f0',
      hover: '#f1f5f9',
      active: '#e2e8f0',
      disabled: '#94a3b8',
      placeholder: '#cbd5e1',
      link: '#3b82f6',
      linkHover: '#2563eb',
    },
  },

  /**
   * 间距系统
   * 统一的间距尺寸，基于8px网格
   */
  spacing: {
    xs: 4, // 超小间距
    sm: 8, // 小间距
    md: 16, // 中等间距
    lg: 24, // 大间距
    xl: 32, // 超大间距
    xxl: 48, // 特大间距
  },

  /**
   * 圆角系统
   */
  borderRadius: {
    none: 0,
    sm: 4,
    md: 8,
    lg: 16,
    xl: 24,
    full: 9999,
  },

  /**
   * 阴影系统
   */
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    card: '0 4px 20px -4px rgba(0, 0, 0, 0.08)',
  },

  /**
   * 字体系统
   */
  typography: {
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  /**
   * Z-Index 层级系统
   */
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },

  /**
   * 过渡动画
   */
  transitions: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },

  /**
   * 断点系统（响应式）
   */
  breakpoints: {
    xs: 480,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1600,
  },
};

/**
 * Ant Design ConfigProvider 主题配置
 *
 * 用于全局配置 Ant Design 组件的主题
 */
export const ANT_DESIGN_THEME_CONFIG = {
  token: {
    // 主色
    colorPrimary: EMERALD_THEME.colors.primary[500],
    colorSuccess: EMERALD_THEME.colors.semantic.success,
    colorWarning: EMERALD_THEME.colors.semantic.warning,
    colorError: EMERALD_THEME.colors.semantic.error,
    colorInfo: EMERALD_THEME.colors.semantic.info,

    // 边框
    borderRadius: EMERALD_THEME.borderRadius.md,

    // 字体
    fontSize: 14,
    fontFamily: EMERALD_THEME.typography.fontFamily.sans,
  },
  components: {
    Button: {
      controlHeight: 36,
      borderRadius: EMERALD_THEME.borderRadius.md,
    },
    Card: {
      borderRadiusLG: EMERALD_THEME.borderRadius.lg,
    },
    Form: {
      labelFontSize: 14,
      verticalLabelPadding: '0 0 8px',
    },
    Input: {
      controlHeight: 36,
      borderRadius: EMERALD_THEME.borderRadius.md,
    },
    Select: {
      controlHeight: 36,
      borderRadius: EMERALD_THEME.borderRadius.md,
    },
    Table: {
      headerBg: EMERALD_THEME.colors.neutral[50],
      headerColor: EMERALD_THEME.colors.neutral[700],
      borderColor: EMERALD_THEME.colors.neutral[200],
    },
  },
};

/**
 * 预设样式组合
 *
 * 常用的样式组合，提高开发效率
 */
export const PRESET_STYLES = {
  /**
   * Card 标准样式
   */
  card: {
    borderRadius: EMERALD_THEME.borderRadius.lg,
    border: `1px solid ${EMERALD_THEME.colors.neutral[200]}`,
    boxShadow: EMERALD_THEME.shadows.card,
  },

  /**
   * Card 头部样式
   */
  cardHeader: {
    background: `linear-gradient(135deg, ${EMERALD_THEME.colors.neutral[50]} 0%, ${EMERALD_THEME.colors.primary[50]} 100%)`,
    borderBottom: `1px solid ${EMERALD_THEME.colors.neutral[200]}`,
    padding: `${EMERALD_THEME.spacing.lg}px ${EMERALD_THEME.spacing.lg}px`,
  },

  /**
   * FormSection 标准样式
   */
  formSection: {
    marginBottom: EMERALD_THEME.spacing.lg,
    padding: EMERALD_THEME.spacing.lg,
    background: EMERALD_THEME.colors.functional.background,
    borderRadius: EMERALD_THEME.borderRadius.md,
    border: `1px solid ${EMERALD_THEME.colors.neutral[200]}`,
  },

  /**
   * 页面容器样式
   */
  pageContainer: {
    padding: EMERALD_THEME.spacing.lg,
    background: EMERALD_THEME.colors.functional.background,
    minHeight: '100vh',
  },

  /**
   * Icon 标准样式
   */
  icon: {
    color: EMERALD_THEME.colors.primary[500],
    fontSize: '16px',
  },
};

/**
 * 工具函数
 */
export const designUtils = {
  /**
   * 获取状态颜色
   */
  getStatusColor: (status: string): string => {
    const statusMap: Record<string, string> = {
      pending: EMERALD_THEME.colors.status.pending,
      in_progress: EMERALD_THEME.colors.status.in_progress,
      completed: EMERALD_THEME.colors.status.completed,
      cancelled: EMERALD_THEME.colors.status.cancelled,
      approved: EMERALD_THEME.colors.status.approved,
      rejected: EMERALD_THEME.colors.status.rejected,
    };
    return statusMap[status] || EMERALD_THEME.colors.neutral[400];
  },

  /**
   * 获取状态对应的 Ant Design Tag 颜色
   */
  getStatusTagColor: (status: string): string => {
    const tagColorMap: Record<string, string> = {
      pending: 'default',
      in_progress: 'processing',
      completed: 'success',
      cancelled: 'error',
      approved: 'success',
      rejected: 'error',
    };
    return tagColorMap[status] || 'default';
  },

  /**
   * 获取语义化颜色
   */
  getSemanticColor: (
    type: 'success' | 'warning' | 'error' | 'info'
  ): string => {
    return EMERALD_THEME.colors.semantic[type];
  },
};

// 导出默认配置
export default EMERALD_THEME;
